import { createPortal } from 'react-dom';

/**
 * Stateless presentational confirmation modal for permanently deleting an event.
 * Extracted verbatim from EventListModule.renderDeleteConfirmationModal (Stage 2 leaf).
 * Holds no state/hooks of its own — all behavior is threaded via props.
 */
export function DeleteConfirmationModal({
  confirmation,
  onCancel,
  onConfirm,
}: {
  confirmation: { eventId: string; title: string } | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!confirmation) return null;

  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '25vh 16px 16px',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '24px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content */}
        <div style={{
          padding: '24px',
        }}>
          <p style={{
            fontSize: '16px',
            color: 'var(--color-text)',
            lineHeight: 1.6,
            margin: 0,
          }}>
            Are you sure you want to permanently delete this event? <strong>This action cannot be undone.</strong>
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '30px 24px 16px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '15px',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              background: 'none',
              color: 'var(--color-text-muted)',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ef4444';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.background = '#b91c1c';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.background = '#dc2626';
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    portalRoot
  );
}
